import "@livekit/components-styles";

import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCollabAnnotatorStore } from "@/lib/collabAnnotator";
import { useCrossSideSounds } from "@/lib/crossSideSounds";
import { mintLiveKitToken } from "@/lib/livekit";
import { ROOM_OPTIONS } from "@/lib/livekitOptions";
import { useWakeLock } from "@/lib/wakeLock";
import { InRoomAnnotation } from "@/modules/annotation";
import { EvidenceAnnotator } from "@/modules/evidence";

import { ConsentHud } from "./ConsentHud";
import { EmbedRoomCommands } from "./EmbedRoomCommands";
import { WaitingRoomOverlay } from "./WaitingRoomOverlay";
import { RoomLayout } from "./RoomLayout";

type Props = {
  sessionId: string;
  livekitRoomSid: string;
  /** Extra controls rendered INSIDE the LiveKitRoom context so they can use
   * useTracks / useLocalParticipant hooks (SnapshotButton lives here). */
  inRoomControls?: ReactNode;
  /** Rendered inside the floating media-control cluster over the video.
   *  SessionPage owns the end-session mutation and passes its button
   *  down, so there is one implementation of ending a session. */
  endSlot?: ReactNode;
  /** Threaded to WaitingRoomOverlay so the "Invite field user" path
   *  can open SessionPage's InviteFieldUserModal. */
  onInviteFieldUser?: () => void;
};

export function OperatorRoom({
  sessionId,
  livekitRoomSid,
  inRoomControls,
  endSlot,
  onInviteFieldUser,
}: Props) {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const collabEvidenceId = useCollabAnnotatorStore((s) => s.openEvidenceId);

  // Keep the operator's laptop awake — sleep mid-session drops the
  // LiveKit data channel + tears down the camera. No-op on browsers
  // without the API. (Removed briefly on 2026-05-17 after a misdiagnosed
  // black-self-view regression; the actual culprit was `h-dvh` on
  // SessionPage — see commit 39650dd. Safe to keep.)
  useWakeLock(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Room name MUST match `_namespaced_room` on the backend (which
        // the egress dispatcher uses) AND the field's join flow
        // (SessionJoinView). Both fall back to `sess-{full session id}`
        // when `livekit_room_sid` is empty. The earlier
        // `sessionId.slice(0, 8)` put the operator in a different room
        // from the field + the egress — symptoms were per-participant
        // egress 404 ("participant not found"), missing cross-side
        // data-channel events (recording badge, sound, switch-camera
        // commands all stayed on the operator's island).
        const r = await mintLiveKitToken({
          room: livekitRoomSid || `sess-${sessionId}`,
          role: "operator",
        });
        if (cancelled) return;
        setToken(r.token);
        setUrl(r.url);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, livekitRoomSid]);

  if (err) return <p className="p-6 text-cvp-danger">{t("ui.operatorRoom.couldNotJoin", { error: err })}</p>;
  if (!token || !url) return <p className="p-6 text-nb-text-2">{t("ui.operatorRoom.connecting")}</p>;

  return (
    <div className="relative h-full">
      <ConsentHud />
      <LiveKitRoom
        token={token}
        serverUrl={url}
        connect
        video
        audio
        options={ROOM_OPTIONS}
        data-lk-theme="default"
        className="h-full"
      >
        <RoomLayout which="operator" sessionId={sessionId} endSlot={endSlot} />
        <RoomAudioRenderer />
        {/* InRoomAnnotation provides the bus context for any toolbar
            that needs it (e.g. OperatorToolbar's annotation popover
            reads from useAnnotationStore + clearAnnotations which both
            ride the bus). The slot's own positioning is the consumer's
            responsibility — OperatorToolbar positions itself as a
            floating bottom-center pill via absolute classes. */}
        <InRoomAnnotation sessionId={sessionId} editable>
          {inRoomControls}
          <CrossSideSoundsListener />
          <EmbedRoomCommands sessionId={sessionId} />
          <WaitingRoomOverlay
            sessionId={sessionId}
            onInviteFieldUser={onInviteFieldUser}
          />
          {collabEvidenceId && (
            <EvidenceAnnotator evidenceId={collabEvidenceId} side="operator" />
          )}
        </InRoomAnnotation>
      </LiveKitRoom>
    </div>
  );
}


/** Mounted inside InRoomAnnotation so the bus is populated. Subscribes
 * to inbound `system/sound.play` envelopes and plays the named chime.
 * Operator-side counterpart to FieldLayout's useCrossSideSounds() call. */
function CrossSideSoundsListener() {
  useCrossSideSounds();
  return null;
}